import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { computed, inject, onMounted, ref, type SetupContext } from "vue";
import { type IValidationErrors } from "../../abstractions/IValidationErrors.ts";
import type { CourseDto } from "../../shared/CourseDto.ts";
import type { CartDto } from "../../shared/CartDto.ts";
import type { CartService } from "../../application/services/CartService.ts";
import type { PartyAddressDto } from "../../shared/PartyAddressDto.ts";
import type { CartPatchDto } from "../../shared/CartPatchDto.ts";
import type { CartPartnerDto } from "../../shared/CartPartnerDto.ts";
import type { CustomerMeDto } from "../../shared/CustomerMeDto.ts";
import type { AddressInput } from "../../shared/AddressDtos.ts";
import PhoneNumberEntry from "../PhoneNumberEntry/index.ts";
import AddressEntry from "../AddressEntry/index.ts";
import { emptyAddress } from "../CheckoutCustomerAddress/index.ts";

interface Props {
  cart: CartDto;
  course: CourseDto;
  me: CustomerMeDto | null;
  validationErrors: IValidationErrors;
}

// Schritt 3b: Tanzpartner. Eingeloggt: Auswahl aus "Meine Tanzpartner" oder neu erfassen.
// Neu: Name, E-Mail, Telefon; Adresse per Haekchen "gleiche Adresse wie ich".
export default {
  template: html,
  components: { PhoneNumberEntry, AddressEntry },
  emits: {
    onChange: (value: CartPatchDto) => typeof value === "object",
    onPartnerAddressChange: (value: PartyAddressDto) => typeof value === "object",
  },
  props: {
    cart: { type: Object, required: true },
    course: { type: Object, required: true },
    me: { type: Object, required: false, default: null },
    validationErrors: { type: Object, required: true },
  },
  setup(props: Props, { emit }: SetupContext) {
    const cartService: CartService | undefined = inject("cartService");
    const model = ref<PartyAddressDto>(emptyAddress());
    const partnerId = ref<number>(0);
    const partnerList = ref<CartPartnerDto[]>([]);
    const sameAddress = ref(true);
    const localError = ref("");

    // Adresse des Bestellers (aus Warenkorb oder Stammdaten) fuer "gleiche Adresse"
    const customerAddress = computed<AddressInput | null>(function () {
      const a = props.cart?.customerAddress;
      if (a && (a.streetName || a.zipCode)) {
        return { countryCode: a.countryCode || "CH", streetName: a.streetName || "", buildingNumber: a.buildingNumber || "", zipCode: a.zipCode || "", city: a.city || "" };
      }
      if (props.me && (props.me.streetName || props.me.zipCode)) {
        return { countryCode: props.me.countryCode || "CH", streetName: props.me.streetName, buildingNumber: props.me.buildingNumber, zipCode: props.me.zipCode, city: props.me.city };
      }
      return null;
    });
    const canUseSameAddress = computed(() => customerAddress.value != null);

    const address = computed<AddressInput>({
      get: () => ({
        countryCode: model.value.countryCode || "CH",
        streetName: model.value.streetName || "",
        buildingNumber: model.value.buildingNumber || "",
        zipCode: model.value.zipCode || "",
        city: model.value.city || "",
      }),
      set: (a: AddressInput) => {
        model.value = { ...model.value, ...a };
      },
    });

    const getError = function (key: string): string {
      const keyInApi = key.charAt(0).toUpperCase() + key.slice(1);
      if (props.validationErrors && keyInApi in props.validationErrors) {
        return props.validationErrors[keyInApi].join(", ");
      }
      return "";
    };

    const choosePartner = function () {
      if (partnerId.value > 0) {
        emit("onChange", { partnerId: partnerId.value } as CartPatchDto);
      }
    };

    const save = function () {
      localError.value = "";
      if (!(model.value.mobile || "").trim() && !(model.value.phoneP || "").trim()) {
        localError.value = "Bitte eine Telefonnummer der zweiten Person angeben.";
        return;
      }
      const dto = { ...model.value };
      if (sameAddress.value && customerAddress.value) {
        Object.assign(dto, customerAddress.value);
      }
      emit("onPartnerAddressChange", dto);
    };

    onMounted(async function () {
      try {
        if (cartService) {
          partnerList.value = await cartService.getPartners();
        }
      } catch (err) {
        console.log(err);
      }
      if (props.cart != null && props.cart.partnerId > 0 && partnerList.value.some((p) => p.customerId == props.cart.partnerId)) {
        partnerId.value = props.cart.partnerId;
      } else if (props.cart != null && props.cart.partnerAddress != null) {
        model.value = { ...emptyAddress(), ...props.cart.partnerAddress };
        const a = props.cart.partnerAddress;
        const c = customerAddress.value;
        sameAddress.value = !!c && a.streetName == c.streetName && a.zipCode == c.zipCode;
      }
      if (!canUseSameAddress.value) {
        sameAddress.value = false;
      }
    });

    return {
      props,
      model,
      address,
      partnerId,
      partnerList,
      sameAddress,
      canUseSameAddress,
      customerAddress,
      localError,
      getError,
      choosePartner,
      save,
    };
  },
};
